import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DocumentApiProvider, useDocumentApi } from '../Services/DocumentApiProvider';
import Breadcrumbs from '../Components/Breadcrumbs';
import SureLoader from '../Components/SureLoader';
import Button from '../Components/Buttons';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { Tabs, Tab } from '../Components/Tabs';
import Graph from '../Components/Graph';
import { formatDateUTC } from '../utils/formatDateUTC';
import {useCachedBlobUrl} from "../Services/useBlobDownloader";

function DocumentDetails() {
    const { organizationId, documentId } = useParams();
    const { getDocumentById, getDocumentContent, getDocumentGraph } = useDocumentApi();
    const [document, setDocument] = useState(null);
    const [content, setContent] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const {getDocumentUrl} = useCachedBlobUrl()
    const [alertShown, setAlertShown] = useState(false);
    const [graph, setGraph] = useState({ loaded: false, data: {} });
    const [documentUrl, setDocumentUrl] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!documentId) return;
            try {
                const url = await getDocumentUrl(documentId);
                if (!alive) return;
                setDocumentUrl(url);
            } catch (e) {
                console.error('getDocumentUrl failed', e);
                if (alive) setDocumentUrl(null);
            }
        })();
        return () => {
            alive = false;
        };
    }, [documentId, getDocumentUrl]);

    useEffect(() => {

        const fetchDocument = async () => {
            try {
                const response = await getDocumentById(documentId);
                setDocument(response);
            } catch (error) {
                console.error('Error fetching document details:', error);
                setDocument(null);
            }
        };

        fetchDocument();
    }, [documentId, getDocumentById]);

    useEffect(() => {
        if (activeTab === 'content') {
            const fetchContent = async () => {
                try {
                    const response = await getDocumentContent(documentId);
                    setContent(response.content);
                    console.log('Content:', response);
                } catch (error) {
                    console.error('Error fetching document content:', error);
                    setContent(null);
                }
            };

            fetchContent();
        } else if (activeTab === 'graph') {
            const fetchGraph = async () => {
                try {
                    const response = await getDocumentGraph(documentId);
                    const graphData = {
                        nodes: response.nodes,
                        links: response.relationships.map(r => ({
                            source: r.from,
                            target: r.to,
                            label: r.id
                        }))
                    };
                    setGraph({ loaded: true, data: graphData });
                } catch (error) {
                    console.error("Error loading graph:", error);
                    setGraph({ loaded: true, data: { nodes: [], links: [] } });
                }
            };

            fetchGraph();
        }
    }, [activeTab, documentId, getDocumentContent, getDocumentGraph]);

    useEffect(() => {
        if (location.state?.successMessage && !alertShown) {
            showAlert('success', location.state.successMessage);
            setAlertShown(true);

            // Clear successMessage from location.state
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

    const renderDocumentDetails = () => (
        <div className="bg-yes border-yes radius-yes p-3 mb-3">
            <p><strong>ID:</strong> {document.id}</p>
            <p><strong>Name:</strong> {document.original_name}</p>
            <p><strong>Type:</strong> {document.type}</p>
            <p><strong>Status:</strong> {document.is_active ? "Active" : "Inactive"}</p>
            <p><strong>Created:</strong> {formatDateUTC(document.created_at)}</p>
            <p><strong>Updated:</strong> {formatDateUTC(document.updated_at)}</p>
            {document.source_type && (
              <p><strong>Source Type:</strong> {document.source_type}</p>
            )}
            <p><strong>Link:</strong> <a href={documentUrl} target="_blank" rel="noopener noreferrer">Open</a></p>
            {document.original_url && (
              <p><strong>Original link:</strong> <a href={document.original_url} target="_blank"
                                                    rel="noopener noreferrer">Open</a></p>
            )}
            <p><strong>Tags:</strong> {document.tags.join(', ')}</p>
        </div>
    );

    const renderDocumentContent = () => (
        <div className="bg-yes border-yes radius-yes p-3 mb-3 int-col-4 d-flex">
            <pre style={ {"text-wrap": "auto"} }>{content}</pre>
        </div>
    );

    const renderGraph = () => (
        <div className="bg-yes border-yes radius-yes m-3 w-100 graph-container"> 
            {graph.loaded ? <Graph graphData={graph.data}/> : <SureLoader />}
        </div>
    );
    const handleCancel = () => {
        navigate(`/organizations/${organizationId}/documents-hub`);
    };

    if (document === null) {
        return <SureLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row">
                <div className="p-2 bg-yes col-8">
                    <h1>Document Details</h1>
                    <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
                        <Tab label="Details" value="details">
                            {renderDocumentDetails()}
                        </Tab>
                        <Tab label="Content" value="content">
                            {renderDocumentContent()}
                        </Tab>
                        <Tab label="Graph" value="graph">
                            {renderGraph()}
                        </Tab>
                    </Tabs>
                    <div className='buttons-container d-flex justify-end'>
                        <Button variant="danger" size="medium" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DocumentDetailsWrapper() {
    return (
        <AlertProvider>
            <DocumentApiProvider>
                <DocumentDetails />
            </DocumentApiProvider>
        </AlertProvider>
    );
}

export default DocumentDetailsWrapper;
